import {
	Alert,
	Autocomplete,
	Box,
	Button,
	Chip,
	Paper,
	Stack,
	Tab,
	Tabs,
	TextField,
	Typography,
} from "@mui/material";
import SaveOutlinedIcon from "@mui/icons-material/SaveOutlined";
import UploadFileOutlinedIcon from "@mui/icons-material/UploadFileOutlined";
import RestartAltOutlinedIcon from "@mui/icons-material/RestartAltOutlined";
import ViewColumnOutlinedIcon from "@mui/icons-material/ViewColumnOutlined";
import { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import { GlobalState } from "context/GlobalContext";
import {
	POS_RECO_MAPPING_MOPS,
	getDefaultMopSchema,
	validateMappingAgainstFile,
} from "./posRecoColumnSchema";
import { autoMatchColumnMap, readPosRecoFileHeaders } from "./posRecoFileColumnUtils";

const COLUMN_MAPPINGS_ENDPOINT = "/api/pos_reco/column_mappings";

const mapFromColumns = (columns = []) =>
	(columns || []).reduce((acc, column) => {
		if (column?.internal_column) acc[column.internal_column] = column.file_header || "";
		return acc;
	}, {});

const ColumnMapping = () => {
	const { setSnackBar, snackBar, setSnackBarOpen, setLoadingOpen } = GlobalState();
	const [mops, setMops] = useState(POS_RECO_MAPPING_MOPS);
	const [activeMop, setActiveMop] = useState(POS_RECO_MAPPING_MOPS[0].id);
	const [fileHeaders, setFileHeaders] = useState([]);
	const [columnMap, setColumnMap] = useState(() => mapFromColumns(POS_RECO_MAPPING_MOPS[0].columns));
	const [sampleName, setSampleName] = useState("");
	const [showOptional, setShowOptional] = useState(true);
	const [isSaved, setIsSaved] = useState(true);
	const [dirty, setDirty] = useState(false);
	const [loadError, setLoadError] = useState("");

	const mopTabs = mops.length ? mops : POS_RECO_MAPPING_MOPS;
	const tabValue = mopTabs.some((mop) => mop.id === activeMop) ? activeMop : mopTabs[0]?.id;

	const activeSchema = useMemo(() => {
		return (
			mopTabs.find((mop) => mop.id === tabValue) ||
			getDefaultMopSchema(tabValue) ||
			POS_RECO_MAPPING_MOPS[0]
		);
	}, [mopTabs, tabValue]);

	const columns = useMemo(() => {
		if (activeSchema?.columns?.length) return activeSchema.columns;
		return getDefaultMopSchema(activeSchema?.id)?.columns || [];
	}, [activeSchema]);

	const applyMopState = useCallback((schema, { keepSample = false } = {}) => {
		const schemaColumns = schema?.columns?.length ? schema.columns : getDefaultMopSchema(schema?.id)?.columns || [];
		setColumnMap(mapFromColumns(schemaColumns));
		if (!keepSample) {
			setFileHeaders([]);
			setSampleName("");
		}
		setIsSaved(true);
		setDirty(false);
	}, []);

	const loadMappings = useCallback(
		async (preferredMop = activeMop) => {
			setLoadingOpen(true);
			try {
				const { data } = await axios.get(COLUMN_MAPPINGS_ENDPOINT);
				const result = Array.isArray(data?.result) ? data.result : [];
				if (!result.length) {
					setLoadError("No column mappings returned from BigQuery.");
					return;
				}
				setMops(result);
				setLoadError("");
				const nextMop = result.some((mop) => mop.id === preferredMop) ? preferredMop : result[0].id;
				setActiveMop(nextMop);
				applyMopState(result.find((mop) => mop.id === nextMop));
			} catch (error) {
				setLoadError(error?.response?.data?.message || "Unable to load column mappings from BigQuery.");
			} finally {
				setLoadingOpen(false);
			}
		},
		[activeMop, applyMopState, setLoadingOpen],
	);

	useEffect(() => {
		loadMappings();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	const validation = useMemo(() => {
		if (!fileHeaders.length) {
			return { ok: true, errors: [], warnings: [], missing: [] };
		}
		const mappedColumns = columns.map((column) => ({
			...column,
			file_header: columnMap[column.internal_column] || "",
		}));
		return validateMappingAgainstFile(mappedColumns, fileHeaders);
	}, [columns, columnMap, fileHeaders]);

	const updateColumnMap = (internalColumn, value) => {
		setColumnMap((prev) => ({ ...prev, [internalColumn]: value || "" }));
		setDirty(true);
		setIsSaved(false);
	};

	const rows = useMemo(() => {
		const list = columns.map((column) => ({
			...column,
			mapped: columnMap[column.internal_column] || "",
		}));
		if (showOptional) return list;
		return list.filter((row) => row.is_required || !row.mapped);
	}, [columns, columnMap, showOptional]);

	const handleTabChange = (_, value) => {
		setActiveMop(value);
		applyMopState(mopTabs.find((mop) => mop.id === value) || getDefaultMopSchema(value));
	};

	const handleSampleFile = async (event) => {
		const file = event.target.files?.[0];
		if (!file) return;
		try {
			setLoadingOpen(true);
			const headers = await readPosRecoFileHeaders(file, activeSchema, columns);
			const matched = autoMatchColumnMap(columns, headers);
			setFileHeaders(headers);
			setColumnMap((prev) => {
				const next = { ...prev };
				Object.entries(matched).forEach(([internalColumn, fileHeader]) => {
					if (fileHeader) next[internalColumn] = fileHeader;
				});
				return next;
			});
			setSampleName(file.name);
			setDirty(true);
			setIsSaved(false);
			setSnackBarOpen(true);
			setSnackBar({
				...snackBar,
				message: headers.length
					? `Loaded ${headers.length} headers from sample file`
					: "No headers found in the sample file",
				severity: headers.length ? "success" : "warning",
				anchorOrigin: { horizontal: "center", vertical: "top" },
			});
		} catch (error) {
			setSnackBarOpen(true);
			setSnackBar({
				...snackBar,
				message: "Unable to read sample file headers",
				severity: "error",
				anchorOrigin: { horizontal: "center", vertical: "top" },
			});
		} finally {
			setLoadingOpen(false);
			event.target.value = "";
		}
	};

	const handleSave = async () => {
		const missingRequired = columns
			.filter((column) => column.is_required && !String(columnMap[column.internal_column] || "").trim())
			.map((column) => column.internal_column);
		if (missingRequired.length) {
			setSnackBarOpen(true);
			setSnackBar({
				...snackBar,
				message: `Map required columns before saving: ${missingRequired.join(", ")}`,
				severity: "error",
				anchorOrigin: { horizontal: "center", vertical: "top" },
			});
			return;
		}
		if (fileHeaders.length && !validation.ok) {
			setSnackBarOpen(true);
			setSnackBar({
				...snackBar,
				message: validation.errors[0] || "Fix mapping errors before saving",
				severity: "error",
				anchorOrigin: { horizontal: "center", vertical: "top" },
			});
			return;
		}

		setLoadingOpen(true);
		try {
			const payload = {
				mop: tabValue,
				columns: columns.map((column) => ({
					internal_column: column.internal_column,
					file_header: columnMap[column.internal_column] || "",
				})),
			};
			const { data } = await axios.put(COLUMN_MAPPINGS_ENDPOINT, payload);
			const saved = data?.result;
			if (saved) {
				setMops((prev) => prev.map((mop) => (mop.id === saved.id ? saved : mop)));
				applyMopState(saved, { keepSample: true });
			}
			setSnackBarOpen(true);
			setSnackBar({
				...snackBar,
				message: data?.message || `${activeSchema?.name || tabValue} column mapping saved`,
				severity: "success",
				anchorOrigin: { horizontal: "center", vertical: "top" },
			});
		} catch (error) {
			setSnackBarOpen(true);
			setSnackBar({
				...snackBar,
				message: error?.response?.data?.message || "Unable to save column mapping",
				severity: "error",
				anchorOrigin: { horizontal: "center", vertical: "top" },
			});
		} finally {
			setLoadingOpen(false);
		}
	};

	const handleResetDefaults = () => {
		if (fileHeaders.length) {
			setColumnMap((prev) => ({ ...prev, ...autoMatchColumnMap(columns, fileHeaders) }));
		} else {
			setColumnMap(mapFromColumns(columns));
		}
		setDirty(true);
		setIsSaved(false);
	};

	const uniqueHeaderOptions = useMemo(
		() => [...new Set((fileHeaders.length ? fileHeaders : Object.values(columnMap)).filter(Boolean))],
		[fileHeaders, columnMap],
	);

	return (
		<Box
			sx={{
				width: "100%",
				height: "100%",
				flex: 1,
				minHeight: 0,
				boxSizing: "border-box",
				p: { xs: 1.5, sm: 2 },
				display: "flex",
				flexDirection: "column",
				overflow: "hidden",
			}}>
			<Paper
				elevation={0}
				sx={{
					flex: 1,
					minHeight: 0,
					display: "flex",
					flexDirection: "column",
					borderRadius: 2.5,
					border: "1px solid",
					borderColor: "divider",
					overflow: "hidden",
				}}>
				<Box sx={{ flex: 1, minHeight: 0, overflowY: "auto", px: { xs: 2.5, md: 3 }, py: 3 }}>
					<Stack direction="row" spacing={2} alignItems="center" mb={2.5}>
						<Box
							sx={{
								width: 42,
								height: 42,
								borderRadius: 2,
								display: "grid",
								placeItems: "center",
								bgcolor: "grey.100",
								border: "1px solid",
								borderColor: "divider",
								flexShrink: 0,
							}}>
							<ViewColumnOutlinedIcon color="primary" />
						</Box>
						<Box>
							<Typography variant="overline" color="text.secondary" sx={{ letterSpacing: 1.1 }}>
								POS Reco
							</Typography>
							<Typography variant="h6" fontWeight={700}>
								File header / column mapping
							</Typography>
							<Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
								Internal dump columns are on the left. Map each one to the header in the vendor file.
								If a file header changes, update it here and save before uploading.
							</Typography>
						</Box>
					</Stack>

					{loadError && (
						<Alert severity="error" sx={{ mb: 2 }}>
							{loadError}
						</Alert>
					)}

					<Tabs
						value={tabValue}
						onChange={handleTabChange}
						variant="scrollable"
						scrollButtons="auto"
						sx={{ borderBottom: 1, borderColor: "divider", mb: 2 }}>
						{mopTabs.map((mop) => (
							<Tab
								key={mop.id}
								value={mop.id}
								label={mop.name}
								sx={{ textTransform: "none", minHeight: 48, fontWeight: 600 }}
							/>
						))}
					</Tabs>

					<Stack direction={{ xs: "column", sm: "row" }} spacing={1.5} mb={2} alignItems={{ sm: "center" }} flexWrap="wrap">
						<Button component="label" variant="outlined" startIcon={<UploadFileOutlinedIcon />} sx={{ textTransform: "none" }}>
							Load sample file headers
							<input hidden type="file" accept=".csv,.xlsx,.xls" onChange={handleSampleFile} />
						</Button>
						<Button variant="outlined" startIcon={<RestartAltOutlinedIcon />} onClick={handleResetDefaults} sx={{ textTransform: "none" }}>
							Reset to current
						</Button>
						<Button variant="contained" startIcon={<SaveOutlinedIcon />} onClick={handleSave} sx={{ textTransform: "none", fontWeight: 700 }}>
							Save mapping
						</Button>
						<Button size="small" onClick={() => setShowOptional((value) => !value)} sx={{ textTransform: "none" }}>
							{showOptional ? "Show required / unmapped" : "Show all columns"}
						</Button>
						{sampleName && <Chip size="small" label={`Sample: ${sampleName}`} sx={{ fontWeight: 600 }} />}
						{activeSchema?.updated_by && (
							<Chip size="small" variant="outlined" label={`Last saved by ${String(activeSchema.updated_by)}`} />
						)}
					</Stack>

					{!validation.ok && (
						<Alert severity="error" sx={{ mb: 2 }}>
							<Typography fontWeight={700} mb={0.5}>
								Mapping errors
							</Typography>
							{validation.errors.map((error) => (
								<Typography key={error} variant="body2">
									• {error}
								</Typography>
							))}
						</Alert>
					)}
					{isSaved && !dirty && (
						<Alert severity="success" sx={{ mb: 2 }}>
							Current mapping is saved in BigQuery. Uploads for this MOP will use these file headers.
						</Alert>
					)}
					{(!isSaved || dirty) && (
						<Alert severity="warning" sx={{ mb: 2 }}>
							This draft is not saved yet. Click <b>Save mapping</b> to update BigQuery.
						</Alert>
					)}

					<Box
						sx={{
							display: "grid",
							gridTemplateColumns: "minmax(220px, 1fr) minmax(260px, 1.2fr)",
							gap: 1,
							px: 0.5,
							pb: 1,
						}}>
						<Typography variant="caption" fontWeight={700} color="text.secondary">
							INTERNAL COLUMN
						</Typography>
						<Typography variant="caption" fontWeight={700} color="text.secondary">
							FILE HEADER
						</Typography>
					</Box>

					<Stack spacing={1}>
						{rows.map((row) => (
							<Box
								key={row.internal_column}
								sx={{
									display: "grid",
									gridTemplateColumns: "minmax(220px, 1fr) minmax(260px, 1.2fr)",
									gap: 1.5,
									alignItems: "center",
									p: 1.25,
									borderRadius: 2,
									border: "1px solid",
									borderColor: row.is_required && !row.mapped ? "#fde68a" : "#e2e8f0",
									bgcolor: row.is_required && !row.mapped ? "#fffbeb" : "#fff",
								}}>
								<Box>
									<Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
										<Typography sx={{ fontSize: 13, fontWeight: 600 }}>{row.internal_column}</Typography>
										{row.is_required && (
											<Chip size="small" label="Required" color="warning" sx={{ height: 20, fontSize: 10 }} />
										)}
									</Stack>
									{row.reason && (
										<Typography variant="caption" color="text.secondary">
											{row.reason}
										</Typography>
									)}
								</Box>
								{fileHeaders.length ? (
									<Autocomplete
										size="small"
										freeSolo
										options={uniqueHeaderOptions}
										value={row.mapped || null}
										isOptionEqualToValue={(option, value) => option === value}
										onChange={(_, value) => updateColumnMap(row.internal_column, value || "")}
										renderInput={(params) => (
											<TextField
												{...params}
												placeholder="Select or type file header"
												error={row.is_required && !row.mapped}
												onBlur={(event) => {
													const typed = event.target.value;
													if (typed !== row.mapped) updateColumnMap(row.internal_column, typed);
												}}
											/>
										)}
									/>
								) : (
									<TextField
										size="small"
										fullWidth
										value={row.mapped}
										placeholder="Current file header"
										error={row.is_required && !row.mapped}
										onChange={(event) => updateColumnMap(row.internal_column, event.target.value)}
									/>
								)}
							</Box>
						))}
					</Stack>
				</Box>
			</Paper>
		</Box>
	);
};

export default ColumnMapping;
